import {
  WSElement,
  WSEmptyState,
  WSModal,
  WSModalCustomProps,
  WSRadioInputGroup,
  WSText,
  useWSModalCustom
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  Currency,
  ListExternalBankAccountsFilterParameterCapabilityEq,
  PaymentFundingSourceType
} from "@wingspanhq/wingspan-api-sdk";
import { queriesExternalBankAccounts, queriesPayables } from "@query";
import React, { useState } from "react";
import { ErrorAlert } from "@components/ErrorAlert";
import { IdempotencyKey } from "../../../../v3/utils/idempotencyKey";

type Props = {
  payableId: string;
  payeeName: string;
  amount: number;
  currency: Currency;
  // Hint from `Payee.defaultPaymentMethod` (read-only today — see the
  // comment on `ClientPayableView.preferredFundingSource`) — preselects the
  // matching bank account below when one is found.
  preferredFundingSource?: {
    type: "ExternalBankAccount" | "PaymentCard";
    id: string;
  };
} & WSModalCustomProps<boolean>;

// Chooses a saved bank account and pays the payable directly — a single,
// synchronous `payPayable` call. ACH only for now: card funding sources
// aren't offered until WIN-15522 confirms `payPayable` supports them.
export const ModalPayInvoice: React.FC<Props> = props => {
  const queryBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery({
      filter: {
        capability: {
          eq: ListExternalBankAccountsFilterParameterCapabilityEq.CHARGE
        }
      }
    });
  const bankAccounts = queryBankAccounts.data?.data ?? [];

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const preferredId =
    props.preferredFundingSource?.type === "ExternalBankAccount"
      ? bankAccounts.find(
          account => account.id === props.preferredFundingSource!.id
        )?.id
      : undefined;
  const currentSelectedId = selectedId ?? preferredId ?? bankAccounts[0]?.id;

  const [payPayable, payPayableMeta] = queriesPayables.payPayable.useMutation();

  const handlePay = async () => {
    if (!currentSelectedId) {
      return;
    }

    const idempotencyKey = new IdempotencyKey({
      payableId: props.payableId,
      fundingSourceId: currentSelectedId
    });

    await payPayable(
      {
        payableId: props.payableId,
        idempotencyKey: idempotencyKey.value,
        payablePaymentRequest: {
          fundingSource: {
            type: PaymentFundingSourceType.EXTERNAL_BANK_ACCOUNT,
            id: currentSelectedId
          }
        }
      },
      {
        onSuccess: () => {
          idempotencyKey.destroy();
          props.onClose(true);
        }
      }
    );
  };

  return (
    <WSModal
      title={`Pay ${props.payeeName}`}
      size="S"
      onClose={() => props.onClose(false)}
      actions={{
        buttons: [
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: () => props.onClose(false)
          },
          {
            kind: "Primary",
            label: `Pay ${formatMoney(props.amount, props.currency)}`,
            onClick: handlePay,
            loading: payPayableMeta.isLoading,
            disabled: !currentSelectedId
          }
        ]
      }}
    >
      {queryBankAccounts.isLoading ? (
        <WSText>Loading your bank accounts...</WSText>
      ) : bankAccounts.length === 0 ? (
        <WSEmptyState
          type="wallet"
          title="No bank account on file"
          description="Add a bank account before paying this invoice."
        />
      ) : (
        <WSElement>
          <WSText.ParagraphSm color="gray500" mb="M">
            Pay from
          </WSText.ParagraphSm>
          <WSRadioInputGroup
            name="fundingSource"
            value={currentSelectedId}
            options={bankAccounts.map(account => ({
              value: account.id,
              label: `${account.nickname || account.accountName}${
                account.accountNumberMask
                  ? ` •••• ${account.accountNumberMask}`
                  : ""
              }`
            }))}
            onChange={(event: any) => {
              setSelectedId(event.target.value);
            }}
          />
        </WSElement>
      )}

      <ErrorAlert
        mt="M"
        error={payPayableMeta.error}
        context="ClientPayInvoice"
      />
    </WSModal>
  );
};

export const useModalPayInvoice = () => useWSModalCustom(ModalPayInvoice);
